import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { CheckCircle, Loader2 } from 'lucide-react';
import { apiPost } from '../lib/api';
import { AuthLayout } from '../components/layout/AuthLayout';

const schema = z.object({
  email: z.string().email('Enter a valid email address'),
});

type FormData = z.infer<typeof schema>;

export function ForgotPasswordPage() {
  const [sent, setSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const onSubmit = async (data: FormData) => {
    try {
      await apiPost('/customer/auth/forgot-password', { email: data.email }, { skipAuth: true });
    } catch {
      // Silently ignore — show confirmation regardless (anti-enumeration)
    }
    setSent(true);
  };

  if (sent) {
    return (
      <AuthLayout title="Check your inbox" subtitle="We've sent a reset link if that email exists">
        <div className="text-center space-y-5">
          <div className="flex justify-center">
            <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center">
              <CheckCircle size={28} className="text-emerald-500" />
            </div>
          </div>
          <p className="text-sm text-zinc-500 leading-relaxed">
            If an account exists for that email address, you'll receive a password reset link
            within a few minutes. Check your spam folder if it doesn't arrive.
          </p>
          <Link to="/login" className="inline-block text-sm font-semibold text-[--color-primary] hover:opacity-80">
            Back to sign in
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Forgot your password?" subtitle="Enter your email and we'll send you a reset link">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-zinc-700 mb-1.5">
            Email address
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            className="input-field"
            placeholder="you@example.com"
            {...register('email')}
          />
          {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
        </div>

        <button type="submit" disabled={isSubmitting} className="btn-primary w-full">
          {isSubmitting ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : 'Send reset link'}
        </button>

        <p className="text-center text-sm text-zinc-500">
          Remembered it?{' '}
          <Link to="/login" className="font-semibold text-[--color-primary] hover:opacity-80">Sign in</Link>
        </p>
      </form>
    </AuthLayout>
  );
}
